import styled from 'styled-components';
import { useRef, useState } from 'react';
import { getWindow } from 'ssr-window';
import { STATES } from '../../constants/states';
import { compose } from '../utils/compose';
import { withStrictMode } from '../utils/with-strict-mode';
import { useDisableBodyScroll } from '../custom-hooks/use-disable-body-scroll';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';
import TextInput from '../shared/FormComponents/TextInput';
import FormSelect from '../shared/FormComponents/FormSelect';
import WidgetIdWrapper, { withWidgetIdWrapper } from '../shared/WidgetIdWrapper';
import BdoModal from '../shared/BdoModal';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';

const EMPTY_FORM_STATE = {
  email: '',
  firstName: '',
  lastName: '',
  company: '',
  title: '',
  city: '',
  state: ''
};

const StyledSubscriptionCta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;
  margin-inline: auto;
  margin-block: 4rem;
  text-align: center;

  .icon {
    margin-right: 1rem;
  }

  p {
    font-size: 2.2rem;
    margin: 0;
    font-weight: 800;
  }

  .btn {
    text-transform: uppercase;
  }
`;

const StyledDialog = styled(BdoModal)`
  border: none;
  position: relative;

  &::backdrop {
    background: rgba(91, 110, 127, 0.5);
  }

  .btn {
    border: none;
    appearance: none;
    text-transform: uppercase;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: min(80rem, 100%);

  .submit {
    border: 0;
    margin-top: 2rem;

    &:disabled {
      cursor: default;
      pointer-events: none;
    }
  }
`;

const StyledTitle = styled.p`
  font-size: 3.2rem;
  line-height: 3.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  margin-top: 0;
`;

const StyledNote = styled.div`
  text-align: center;

  p {
    margin-block: 0;
  }
`;

const StyledDoubleInputContainer = styled.div`
  display: grid;
  grid: auto-flow / repeat(auto-fit, minmax(20rem, 1fr));
  gap: 2rem;
`;

const ResponseContainer = styled.div`
  display: grid;
  grid: auto auto / 1fr;
  gap: 3rem;
  width: min(80rem, 100%);
`;

const SubscriptionComponent = ({
  properties,
  className,
  ctaTitle,
  ctaButtonText,
  formTitle,
  loginSuggestions,
  isLoggedIn,
  recaptchaSiteKey,
  subscriptionId
}) => {
  const { widgetID } = properties;
  const formRef = useRef(null);
  const { resourceStrings } = useLocalization();
  const [formState, setFormState] = useState(EMPTY_FORM_STATE);
  const [isOpen, setIsOpen] = useState(false);
  const [subscriptionResponseText, setSubscriptionResponseText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const window = getWindow();

  useDisableBodyScroll(isOpen);

  const closeModal = (event) => {
    event.preventDefault();
    setIsOpen(false);
    setSubscriptionResponseText('');
  };

  const handleSubscribe = async (shouldBypass) => {
    if (isLoggedIn || shouldBypass) {
      setIsSubmitting(true);
      setIsOpen(true);
      try {
        const token = await new Promise((resolve, reject) =>
          window.grecaptcha.enterprise.ready(() => {
            window.grecaptcha.enterprise
              .execute(recaptchaSiteKey, {
                action: 'Subscription'
              })
              .then(resolve)
              .catch(reject);
          })
        );

        const body = {
          ...formState,
          gToken: token,
          subscriptionId
        };
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        headers.append(
          'RequestVerificationToken',
          document.getElementsByName('__RequestVerificationToken')[0].value
        );
        const subscriptionResponse = await fetch('/api/Subscribe', {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        });

        if (subscriptionResponse.ok) {
          setSubscriptionResponseText(resourceStrings['bdO.SubscriptionWidget.Submission.Success']);
        } else {
          setSubscriptionResponseText(resourceStrings['bdO.SubscriptionWidget.Submission.Error']);
        }
      } catch (e) {
        setSubscriptionResponseText(resourceStrings['bdO.SubscriptionWidget.Submission.Error']);
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    setIsOpen(true);
  };

  /** @type {React.ReactEventHandler<HTMLDialogElement>} */
  const handleSubmit = async (event) => {
    event.preventDefault();

    const isValid = formRef.current.checkValidity();
    formRef.current.checkValidity();

    if (!isValid) return;

    await handleSubscribe(true);
    setFormState(EMPTY_FORM_STATE);
  };

  /** @type {React.FormEventHandler<HTMLInputElement>} */
  const handleInput = (event) => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      [event.target.name]: event.target.value
    }));
  };

  return (
    <WidgetIdWrapper widgetID={widgetID} className={className}>
      <div className="container-padding-md">
        <StyledSubscriptionCta>
          <p>{ctaTitle}</p>
          <button
            type="button"
            className="btn btn-secondary cta"
            onClick={() => handleSubscribe(false)}
          >
            <span className="icon material-icons-outlined">mail</span>
            {ctaButtonText}
          </button>
        </StyledSubscriptionCta>
      </div>
      <StyledDialog
        showModal={isOpen}
        closeModal={closeModal}
        setShowModal={setIsOpen}
        classProp="bdo-modal simple"
      >
        {isLoggedIn && !subscriptionResponseText && (
          <ResponseContainer>
            <StyledTitle>
              {resourceStrings['bdO.SubscriptionWidget.SubmitButton.Loading']}
            </StyledTitle>
          </ResponseContainer>
        )}
        {subscriptionResponseText && (
          <ResponseContainer>
            <StyledTitle>{subscriptionResponseText}</StyledTitle>
          </ResponseContainer>
        )}
        {!isLoggedIn && !subscriptionResponseText && (
          <StyledForm
            ref={formRef}
            method="dialog"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();

                if (!isSubmitting) handleSubmit(event);
              }
            }}
          >
            <div>
              <StyledTitle>{formTitle}</StyledTitle>
              <StyledNote
                suppressHydrationWarning
                dangerouslySetInnerHTML={{ __html: loginSuggestions }}
              />
            </div>
            <TextInput
              id="email"
              name="email"
              label="Email"
              type="email"
              autoComplete="email"
              pattern="[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+"
              onInput={handleInput}
              value={formState.email}
              disabled={isSubmitting}
              errorMessages={{
                patternMismatch: 'Please enter a valid email address.',
                typeMismatch: 'Please enter a valid email address.'
              }}
              autoFocus
              required
            />
            <StyledDoubleInputContainer>
              <TextInput
                id="first-name"
                name="firstName"
                email="first-name"
                label={resourceStrings['bdO.SubscriptionWidget.FirstNameLabel']}
                autoComplete="given-name"
                onInput={handleInput}
                value={formState.firstName}
                disabled={isSubmitting}
                required
              />
              <TextInput
                id="last-name"
                name="lastName"
                label={resourceStrings['bdO.SubscriptionWidget.LastNameLabel']}
                autoComplete="family-name"
                value={formState.lastName}
                onInput={handleInput}
                disabled={isSubmitting}
                required
              />
            </StyledDoubleInputContainer>
            <StyledDoubleInputContainer>
              <TextInput
                id="company"
                name="company"
                label={resourceStrings['bdO.SubscriptionWidget.OrganizationLabel']}
                autoComplete="organization"
                value={formState.company}
                onInput={handleInput}
                disabled={isSubmitting}
                required
              />
              <TextInput
                id="title"
                name="title"
                label={resourceStrings['bdO.SubscriptionWidget.OrganizationTitleLabel']}
                autoComplete="organization-title"
                value={formState.title}
                onInput={handleInput}
                disabled={isSubmitting}
                required
              />
            </StyledDoubleInputContainer>
            <StyledDoubleInputContainer>
              <TextInput
                id="city"
                name="city"
                label={resourceStrings['bdO.SubscriptionWidget.CityLabel']}
                value={formState.city}
                onInput={handleInput}
                disabled={isSubmitting}
                required
              />
              <FormSelect
                label={resourceStrings['bdO.SubscriptionWidget.StateLabel']}
                name="state"
                selectId="state"
                autoComplete="address-level1"
                placeholder="State"
                selectedOption={formState?.state}
                disabled={isSubmitting}
                onChangeFunction={handleInput}
                options={Object.entries(STATES).map(([key, value]) => ({
                  value: key,
                  name: value
                }))}
                required
              />
            </StyledDoubleInputContainer>
            <button
              data-sitekey="test"
              className="btn btn-primary submit"
              value="submit"
              disabled={isSubmitting}
              onClick={(event) => {
                if (!isSubmitting) handleSubmit(event);
              }}
            >
              {isSubmitting
                ? resourceStrings['bdO.SubscriptionWidget.SubmitButton.Loading']
                : resourceStrings['bdO.SubscriptionWidget.SubmitButton']}
            </button>
          </StyledForm>
        )}
      </StyledDialog>
    </WidgetIdWrapper>
  );
};

export default compose(
  withWidgetIdWrapper,
  withLocalization,
  withStrictMode,
  withWidgetSpacingWrapper
)(SubscriptionComponent);

/**
 * @typedef {{
 *   userGuid: string;
 *   gToken: string;
 *   email: string;
 *   firstName: string;
 *   lastName: string;
 *   company: string;
 *   title: string;
 *   city: string;
 *   state: string;
 *   subscriptionId: string;
 * }} Subscriber
 */
